// src/component/Admin/QuizSessions.tsx
import React from 'react';
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { db } from '../../firebase';
import { Quiz } from '../../types/quiz.types';
import { useAuth } from '../../context/Auth/auth-context';

export const QuizSessions = () => {
  const { quizId } = useParams();
  const { user, userInfo } = useAuth();
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [sessions, setSessions] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!quizId) return;

    const quizRef = ref(db, `quizzes/${quizId}`);
    const unsubscribe = onValue(quizRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setQuiz(data);
      }
    });

    return () => unsubscribe();
  }, [quizId]);

  useEffect(() => {
    if (!quizId) return;

    const sessionsRef = ref(db, 'sessions');
    const unsubscribe = onValue(sessionsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        // Filter sessions for this quiz. Presenters only see sessions THEY
        // host — a catalog quiz is shared, its sessions are not.
        const isAdmin = userInfo?.role === 'admin';
        const filteredSessions = Object.entries(data).reduce((acc, [key, session]: [string, any]) => {
          if (session.quizId === quizId && (isAdmin || session.hostUid === user)) {
            acc[key] = session;
          }
          return acc;
        }, {} as Record<string, any>);
        setSessions(filteredSessions);
      }
    });

    return () => unsubscribe();
  }, [quizId, user, userInfo?.role]);

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'not_started':
        return 'bg-accent/30 text-ink';
      case 'in_progress':
        return 'bg-tile-green text-white';
      case 'ended':
        return 'bg-ink/10 text-ink/60';
      default:
        return 'bg-ink/10 text-ink/60';
    }
  };

  if (!quiz) {
    return (<div className="p-4">Loading...</div>);
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="mb-6">
        <Link to="/admin/quizzes" className="text-ink font-semibold underline decoration-2 decoration-accent underline-offset-2">
          ← Back to Quizzes
        </Link>
        <h2 className="text-2xl font-bold font-display tracking-tight mt-2">{quiz.title} - Sessions</h2>
      </div>

      <div className="space-y-4">
        {Object.entries(sessions).length === 0 ? (
          <p className="text-slate-400 text-center py-10">No sessions yet — host this quiz to start one.</p>
        ) : (
          Object.entries(sessions)
            // Newest first — the session you just created is the one you want.
            .sort(([, a], [, b]) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
            .map(([sessionId, session]) => (
            <div key={sessionId} className="card p-5 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold font-display tabular-nums tracking-wider">{sessionId}</h3>
                  <span className={`chip ${getStatusBadgeClass(session.status)}`}>
                    {session.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Created {new Date(session.createdAt).toLocaleString()}
                </p>
              </div>
              <Link
                to={`/admin/session/${sessionId}`}
                className="btn-primary text-sm shrink-0"
              >
                Manage session
              </Link>
            </div>
            ))
        )}
      </div>
    </div>
  );
};